import { Vec3 } from '@/core/math/Vec3';
import { clamp, clamp01 } from '@/core/math/scalar';
import { Engine, EngineEnvironment } from './Propulsion';

export interface AllocationDemand {
  /** Desired force in body coordinates, newtons. */
  force: Vec3;
  /** Desired torque in body coordinates, newton-metres. */
  torque: Vec3;
}

export interface AllocationResult {
  /** Achieved force in body coordinates, N. */
  force: Vec3;
  /** Achieved torque in body coordinates, N m. */
  torque: Vec3;
  /** Fraction of the torque demand met, 0..1. */
  torqueSatisfaction: number;
  /** Fraction of the force demand met, 0..1. */
  forceSatisfaction: number;
  /** True when the demand exceeded what the thrusters can produce. */
  saturated: boolean;
}

interface Actuator {
  engine: Engine;
  /** Unit thrust direction in body coordinates. */
  dir: Vec3;
  /** Offset from the centre of mass in body coordinates, metres. */
  arm: Vec3;
  /** Force contribution at full throttle, N. */
  force: Vec3;
  /** Torque contribution at full throttle, N m. */
  torque: Vec3;
  /** Squared magnitude of the weighted 6-vector contribution. */
  normSq: number;
  throttle: number;
}

/**
 * Distributes a desired body-frame force and torque across the available
 * reaction-control thrusters.
 *
 * This is the layer that makes "the flight computer holds attitude" an actual
 * physical operation rather than a direct write to the orientation. The
 * controller produces a *demand*; this class decides which thrusters fire and
 * how hard, subject to each thruster only being able to push one way.
 *
 * The solver is a non-negative Gauss-Seidel least-squares pass over the
 * actuators, run for a few iterations. It is not optimal, but it is fast,
 * allocation-free after warm-up, and it degrades sensibly: when the demand
 * cannot be met the result is the closest achievable wrench rather than a
 * failure, which is exactly what a real allocator does.
 */
export class ControlAllocator {
  private actuators: Actuator[] = [];
  private readonly residual = new Float64Array(6);
  private readonly achieved = new Float64Array(6);

  /** Relative weighting of torque error against force error in the solve. */
  torqueWeight = 1;
  forceWeight = 1;
  iterations = 4;

  /**
   * Rebuilds the actuator table. Must be called whenever the centre of mass
   * moves appreciably (fuel burn, cargo transfer) or engines are damaged.
   */
  configure(engines: Engine[], centreOfMass: Vec3, env: EngineEnvironment, referenceLength: number): void {
    this.actuators = [];
    const scratch = new Vec3();
    for (const e of engines) {
      if (!e.def.attitudeCapable || !e.enabled || e.damage >= 1) continue;
      const maxThrust = e.maxThrustAt(env);
      if (maxThrust <= 0) continue;
      const dir = e.thrustDirection(scratch).clone();
      const arm = Vec3.subVectors(e.mountPosition, centreOfMass);
      const force = dir.clone().mulScalar(maxThrust);
      const torque = Vec3.crossVectors(arm, force);
      this.actuators.push({
        engine: e,
        dir,
        arm,
        force,
        torque,
        normSq: 0,
        throttle: 0,
      });
    }

    // Normalise so force and torque contribute comparably to the residual.
    const L = Math.max(1, referenceLength);
    for (const a of this.actuators) {
      const fx = a.force.x * this.forceWeight;
      const fy = a.force.y * this.forceWeight;
      const fz = a.force.z * this.forceWeight;
      const tx = (a.torque.x / L) * this.torqueWeight;
      const ty = (a.torque.y / L) * this.torqueWeight;
      const tz = (a.torque.z / L) * this.torqueWeight;
      a.normSq = fx * fx + fy * fy + fz * fz + tx * tx + ty * ty + tz * tz;
    }
  }

  get actuatorCount(): number {
    return this.actuators.length;
  }

  /**
   * Solves for thruster throttles. Writes `commandedThrottle` on each engine
   * that participates and returns the wrench actually produced.
   */
  solve(demand: AllocationDemand, referenceLength: number): AllocationResult {
    const L = Math.max(1, referenceLength);
    for (const a of this.actuators) a.throttle = 0;

    this.residual[0] = demand.force.x * this.forceWeight;
    this.residual[1] = demand.force.y * this.forceWeight;
    this.residual[2] = demand.force.z * this.forceWeight;
    this.residual[3] = (demand.torque.x / L) * this.torqueWeight;
    this.residual[4] = (demand.torque.y / L) * this.torqueWeight;
    this.residual[5] = (demand.torque.z / L) * this.torqueWeight;

    for (let iter = 0; iter < this.iterations; iter++) {
      for (const a of this.actuators) {
        if (a.normSq <= 0) continue;
        const c0 = a.force.x * this.forceWeight;
        const c1 = a.force.y * this.forceWeight;
        const c2 = a.force.z * this.forceWeight;
        const c3 = (a.torque.x / L) * this.torqueWeight;
        const c4 = (a.torque.y / L) * this.torqueWeight;
        const c5 = (a.torque.z / L) * this.torqueWeight;

        const dot =
          this.residual[0] * c0 +
          this.residual[1] * c1 +
          this.residual[2] * c2 +
          this.residual[3] * c3 +
          this.residual[4] * c4 +
          this.residual[5] * c5;

        let delta = dot / a.normSq;
        // A thruster can only push, and only up to full throttle.
        delta = clamp(delta, -a.throttle, 1 - a.throttle);
        if (delta === 0) continue;
        a.throttle += delta;

        this.residual[0] -= delta * c0;
        this.residual[1] -= delta * c1;
        this.residual[2] -= delta * c2;
        this.residual[3] -= delta * c3;
        this.residual[4] -= delta * c4;
        this.residual[5] -= delta * c5;
      }
    }

    this.achieved.fill(0);
    const force = new Vec3();
    const torque = new Vec3();
    for (const a of this.actuators) {
      const u = clamp01(a.throttle);
      a.engine.commandedThrottle = Math.max(a.engine.commandedThrottle, u);
      force.addScaled(a.force, u);
      torque.addScaled(a.torque, u);
    }

    const demandedTorque = demand.torque.length();
    const demandedForce = demand.force.length();
    const alignedTorque = demandedTorque > 1e-9 ? Math.max(0, torque.dot(demand.torque) / demandedTorque) : 0;
    const alignedForce = demandedForce > 1e-9 ? Math.max(0, force.dot(demand.force) / demandedForce) : 0;

    const torqueSatisfaction = demandedTorque > 1e-9 ? clamp01(alignedTorque / demandedTorque) : 1;
    const forceSatisfaction = demandedForce > 1e-9 ? clamp01(alignedForce / demandedForce) : 1;

    return {
      force,
      torque,
      torqueSatisfaction,
      forceSatisfaction,
      saturated: torqueSatisfaction < 0.98 || forceSatisfaction < 0.98,
    };
  }

  /** Peak torque available about a body axis, N m. Used for controller gains. */
  maxTorqueAbout(axis: Vec3): number {
    const a = axis.normalized();
    let total = 0;
    for (const act of this.actuators) {
      const c = act.torque.dot(a);
      if (c > 0) total += c;
    }
    return total;
  }

  /** Peak force available along a body axis, N. */
  maxForceAlong(axis: Vec3): number {
    const a = axis.normalized();
    let total = 0;
    for (const act of this.actuators) {
      const c = act.force.dot(a);
      if (c > 0) total += c;
    }
    return total;
  }
}
